import React from "react";
import { motion } from "framer-motion";
import {
  Zap, Repeat, Users, LayoutDashboard, Network, GitFork, ArrowUpRight, Building2
} from "lucide-react";
import { ABOUT_IMAGE, ABOUT_FEATURES, BRAND } from "../lib/constants";

const ICON_MAP = { Zap, Repeat, Users, LayoutDashboard, Network, GitFork };

const STATS = [
  { v: "12+", l: "Years building FX infrastructure" },
  { v: "40M+", l: "Daily trade volume handled" },
  { v: "30+", l: "Jurisdictions supported" },
  { v: "99.99%", l: "System availability" },
];

export default function About() {
  return (
    <section
      id="about"
      data-testid="about-section"
      className="relative theme-section py-24 sm:py-32 overflow-hidden"
    >
      <div className="absolute inset-0 dot-grid opacity-40 pointer-events-none" />
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#00C2FF]/50 to-transparent" />
      <div className="absolute -bottom-32 left-1/3 w-[480px] h-[480px] bg-[#003E8C] rounded-full blur-[200px] opacity-40" />

      <div className="max-w-7xl mx-auto px-6 relative">
        {/* Header — matches Services eyebrow, not giant number */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="max-w-3xl mb-14 sm:mb-20"
        >
          <div className="flex items-center gap-3 mb-5">
            <span className="w-12 h-px bg-[#00C2FF]" />
            <span className="font-mono text-[11px] uppercase tracking-[0.4em] text-[#00C2FF] font-bold">
              / About
            </span>
          </div>
          <h2 className="font-display text-3xl md:text-5xl lg:text-6xl font-extrabold leading-[1.05] tracking-tighter">
            Platform technology for brokers
            <br />
            <span className="text-gradient-cyan italic">built to keep pace with your desk.</span>
          </h2>
          <p className="text-gray-400 text-base leading-relaxed mt-6">
            {BRAND.name} delivers the connective stack between your clients and live markets: CRM,
            liquidity connectivity, copy trading, risk tooling, and bespoke applications — free from
            outdated platform baggage.
          </p>
        </motion.div>

        {/* Split: image + floating badge | feature bento */}
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-14 items-start">
          <motion.div
            initial={{ opacity: 0, x: -24 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="lg:col-span-5 relative"
          >
            <div className="relative rounded-2xl overflow-hidden border border-white/10 shadow-[0_24px_80px_rgba(0,194,255,0.12)]">
              <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-[#00C2FF] via-[#9FE3FF] to-transparent z-10" />
              <img
                src={ABOUT_IMAGE}
                alt={`About ${BRAND.name}`}
                className="w-full aspect-[4/5] object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#000000]/80 via-transparent to-transparent" />
            </div>

            <motion.div
              initial={{ opacity: 0, scale: 0.92 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.25, duration: 0.5 }}
              className="relative sm:absolute sm:-bottom-5 sm:-right-4 sm:right-6 mt-4 sm:mt-0 glass-panel rounded-2xl p-5 sm:p-6 max-w-[220px] border-[#00C2FF]/25"
            >
              <Building2 className="w-5 h-5 text-[#00C2FF] mb-2" />
              <p className="font-display text-sm font-bold theme-heading leading-snug">
                Shaped by people who have opened trading floors — not consultants who only present them.
              </p>
              <p className="text-[10px] uppercase tracking-[0.2em] text-gray-500 mt-2 font-mono">
                Founded 2012
              </p>
            </motion.div>
          </motion.div>

          <div className="lg:col-span-7">
            <div className="grid sm:grid-cols-2 gap-4">
              {ABOUT_FEATURES.map((f, i) => {
                const Icon = ICON_MAP[f.icon] || Zap;
                return (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, y: 16 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: i * 0.05, duration: 0.45 }}
                    data-testid={`about-feature-${i}`}
                    className="group glass-panel-light rounded-2xl p-5 hover:border-[#00C2FF]/40 transition-colors duration-300"
                  >
                    <div className="flex items-start justify-between gap-3 mb-3">
                      <div className="w-10 h-10 rounded-lg bg-[#00C2FF]/10 border border-[#00C2FF]/25 flex items-center justify-center text-[#00C2FF]">
                        <Icon className="w-4 h-4" />
                      </div>
                      <span className="font-mono text-[10px] text-gray-600 tabular-nums">
                        {String(i + 1).padStart(2, "0")}
                      </span>
                    </div>
                    <p className="text-sm text-gray-300 leading-relaxed group-hover:text-slate-900 dark:group-hover:text-white transition-colors">
                      {f.text}
                    </p>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Stats — standalone metric strip (not overlaid on image) */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="mt-16 sm:mt-20 grid grid-cols-2 lg:grid-cols-4 gap-4"
        >
          {STATS.map((s, i) => (
            <div
              key={s.l}
              className="relative rounded-2xl border border-white/10 bg-gradient-to-br from-[#001a2e]/80 to-black/90 px-6 py-8 overflow-hidden"
            >
              <div className="absolute top-0 right-0 w-24 h-24 bg-[#00C2FF]/10 rounded-full blur-2xl" />
              <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#00C2FF]/80 mb-2">
                {String(i + 1).padStart(2, "0")}
              </div>
              <div className="font-display text-3xl sm:text-4xl font-black theme-heading">
                {s.v}
              </div>
              <div className="text-xs text-gray-500 mt-1">{s.l}</div>
            </div>
          ))}
        </motion.div>

        {/* CTA — outline style, distinct from old gradient pill */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          className="mt-14 flex flex-col sm:flex-row sm:items-center gap-6"
        >
          <a
            href="#contact"
            data-testid="about-cta"
            className="inline-flex items-center justify-center gap-2 border border-[#00C2FF]/50 text-[#9FE3FF] hover:bg-[#00C2FF]/10 hover:border-[#00C2FF] px-7 py-3.5 rounded-full font-semibold transition-all duration-300"
          >
            Speak with engineering
            <ArrowUpRight className="w-4 h-4" />
          </a>
          <p className="text-sm text-gray-500 max-w-md">
            From scoping through launch, the same specialists who maintain our broker platform
            implement yours.
          </p>
        </motion.div>
      </div>
    </section>
  );
}
